import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { C, card, label, btn, input, THEME_LIST } from "../lib/theme";
import ChartWheel from "../lib/ChartWheel";
import { POLICY_VERSION } from "../lib/policy";

const AV_GLYPH = { "planet-sun":"\u2609","planet-moon":"\u263D","planet-mercury":"\u263F",
  "planet-venus":"\u2640","planet-mars":"\u2642","planet-jupiter":"\u2643",
  "planet-saturn":"\u2644","planet-uranus":"\u2645","planet-neptune":"\u2646",
  "planet-pluto":"\u2647","lot-spirit":"\u2726","lot-fortune":"\u2297",
  "sign-aries":"\u2648","sign-taurus":"\u2649","sign-gemini":"\u264A","sign-cancer":"\u264B",
  "sign-leo":"\u264C","sign-virgo":"\u264D","sign-libra":"\u264E","sign-scorpio":"\u264F",
  "sign-sagittarius":"\u2650","sign-capricorn":"\u2651","sign-aquarius":"\u2652","sign-pisces":"\u2653" };

export default function Profile({ me, refresh, theme, onTheme }) {
  const [presets, setPresets] = useState([]);
  const [selfChart, setSelfChart] = useState(null);
  useEffect(() => { api.charts().then((cs) => setSelfChart(cs.find((c) => c.is_self))); }, []);
  const [f, setF] = useState({});
  const [msg, setMsg] = useState("");
  const [consent, setConsent] = useState(null);   // latest research_aggregate row or false
  useEffect(() => { api.consents().then((cs) =>
    setConsent(cs.find((c) => c.kind === "research_aggregate") || false)).catch(() => {}); }, []);
  const recordConsent = (granted) =>
    api.setConsent({ kind: "research_aggregate", policy_version: POLICY_VERSION, granted })
      .then(() => api.consents().then((cs) =>
        setConsent(cs.find((c) => c.kind === "research_aggregate") || false)))
      .catch((e) => setMsg(e.message));
  useEffect(() => { api.avatars().then((a) => setPresets(a.presets)); }, []);
  useEffect(() => { if (me) setF({ display_name: me.display_name || "",
    signature_url: me.signature_url || "", chart_privacy: me.chart_privacy,
    show_age: me.show_age, show_location: me.show_location,
    mbti: me.self_reported_type || "" }); }, [me]);
  if (!me) return null;
  const save = (patch) => api.profile(patch).then(() => { setMsg("Saved."); refresh(); })
    .catch((e) => setMsg(e.message));
  return (
    <div style={{ maxWidth: 720 }}>
      {selfChart && (
        <div style={{ ...card, marginBottom: 12, textAlign: "center" }}>
          <div style={{ ...label, textAlign: "left" }}>YOUR CHART · WHOLE SIGN</div>
          <ChartWheel positions={selfChart.positions} lots={selfChart.lots}
            asc={selfChart.asc_sign} ascLon={selfChart.asc_lon} size={360} />
          <div style={{ fontSize: 11, color: C.faint, marginTop: 4 }}>
            {selfChart.asc_sign} rising · {selfChart.sect} chart — what others see is
            governed by your privacy level below
          </div>
        </div>
      )}
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={label}>IDENTITY</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input value={f.display_name || ""} placeholder="Display name"
            onChange={(e) => setF({ ...f, display_name: e.target.value })} style={{ ...input, flexGrow: 1 }} />
          <button style={btn()} onClick={() => save({ display_name: f.display_name })}>Save</button>
        </div>
        {me.plan === "professional" && (
          <div style={{ display: "flex", gap: 8 }}>
            <input value={f.signature_url || ""} placeholder="https://your-practice.example"
              onChange={(e) => setF({ ...f, signature_url: e.target.value })} style={{ ...input, flexGrow: 1 }} />
            <button style={btn()} onClick={() => save({ signature_url: f.signature_url })}>Save signature</button>
          </div>)}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(12,1fr)", gap: 5, marginTop: 12 }}>
          {presets.map((p) => (
            <button key={p} title={p} onClick={() => api.setAvatar(p).then(refresh)}
              style={{ width: 30, height: 30, borderRadius: "50%", cursor: "pointer",
                background: me.avatar_ref === p ? C.brass : C.surface2,
                color: me.avatar_ref === p ? C.bg : C.brass,
                border: `1px solid ${me.avatar_ref === p ? C.brass : C.line}`, fontSize: 14 }}>
              {AV_GLYPH[p] || "?"}</button>))}
        </div>
      </div>
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={label}>CHART PRIVACY</div>
        {[["none","No information"],["big3","Only my Big 3 (default)"],
          ["placements","Only my placements"],["full","Full chart (degrees, whole-sign wheel)"]]
          .map(([k, l]) => (
          <label key={k} style={{ display: "flex", gap: 8, alignItems: "center",
            fontSize: 12.5, padding: "3px 0", cursor: "pointer" }}>
            <input type="radio" checked={f.chart_privacy === k}
              onChange={() => { setF({ ...f, chart_privacy: k }); save({ chart_privacy: k }); }}
              style={{ accentColor: C.brass }} />{l}</label>))}
        {[["show_age", "Show my age (from chart)"], ["show_location", "Show my location"]]
          .map(([k, l]) => (
          <label key={k} style={{ display: "flex", gap: 8, alignItems: "center",
            fontSize: 12.5, padding: "3px 0", cursor: "pointer" }}>
            <input type="checkbox" checked={!!f[k]}
              onChange={(e) => { setF({ ...f, [k]: e.target.checked }); save({ [k]: e.target.checked }); }}
              style={{ accentColor: C.brass }} />{l}</label>))}
      </div>
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={label}>APPEARANCE</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
          gap: 8 }}>
          {THEME_LIST.map((t) => (
            <button key={t.key} onClick={() => onTheme(t.key)}
              style={{ textAlign: "left", cursor: "pointer", fontFamily: "inherit",
                background: C.surface2, borderRadius: 6, padding: "10px 12px",
                border: `1px solid ${theme === t.key ? C.brass : C.line}` }}>
              <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
                {t.swatch.map((c, i) => (
                  <span key={i} style={{ width: 16, height: 16, borderRadius: 4,
                    background: c, border: `1px solid ${C.line}` }} />))}
              </div>
              <div style={{ fontSize: 12.5, color: C.text, fontWeight: 600 }}>
                {t.label}{theme === t.key && <span style={{ color: C.brass }}> ✓</span>}</div>
              <div style={{ fontSize: 10.5, color: C.faint }}>{t.desc}</div>
            </button>))}
        </div>
        <div style={{ fontSize: 10.5, color: C.faint, marginTop: 6 }}>
          Applies instantly, saved on this device.
        </div>
      </div>
      <div style={card}>
        <div style={label}>RESEARCH CONSENT</div>
        <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.6, marginBottom: 8 }}>
          Include your logged events, assessments, and partner entries in anonymous,
          aggregate research (never your notes or anyone's birth details).
        </div>
        {consent === null ? <div style={{ fontSize: 12, color: C.faint }}>…</div> : <>
          <div style={{ fontSize: 12, marginBottom: 8 }}>
            {consent && consent.granted
              ? <>Currently: <b style={{ color: C.brass }}>opted in</b>
                  <span style={{ color: C.faint }}> under policy {consent.policy_version}</span>
                  {consent.policy_version < POLICY_VERSION &&
                    <span style={{ color: C.rose }}> — a newer policy ({POLICY_VERSION}) covers
                      partner entries; renew to include them</span>}</>
              : <>Currently: <b>not opted in</b></>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {(!consent || !consent.granted || consent.policy_version < POLICY_VERSION) &&
              <button style={btn()} onClick={() => recordConsent(true)}>
                {consent && consent.granted ? `Renew under ${POLICY_VERSION}` : "Opt in"}</button>}
            {consent && consent.granted &&
              <button style={btn(false)} onClick={() => recordConsent(false)}>Withdraw</button>}
          </div>
        </>}
      </div>
      <div style={card}>
        <div style={label}>PERSONALITY TYPE (SELF-REPORTED)</div>
        <div style={{ fontSize: 11, color: C.faint, marginBottom: 6 }}>
          Four-letter type from any typology test you've taken elsewhere.
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={f.mbti || ""} placeholder="e.g. INTJ" maxLength={4}
            onChange={(e) => setF({ ...f, mbti: e.target.value.toUpperCase() })}
            style={{ ...input, width: 90 }} />
          <button style={btn()} onClick={() => api.setType(f.mbti)
            .then(() => setMsg("Type saved.")).catch((e) => setMsg(e.message))}>Save</button>
        </div>
      </div>
      {msg && <div style={{ color: C.brass, fontSize: 12, marginTop: 10 }}>{msg}</div>}
    </div>
  );
}
